import React from 'react'
import { Plus, Trash2, MessageCircle, Star, Heart } from 'lucide-react'
import { useAuth } from '../context/AuthContext'

// Clean, minimal sidebar - chat history focused
export default function Sidebar({ 
  open, 
  onToggle, 
  conversations, 
  activeConversation, 
  onNewChat, 
  onSelectConversation,
  onDeleteConversation,
  user,
  onSignIn,
  onSignOut,
  isMobile = false
}) {
  const { isPro } = useAuth()

  return (
    <>
      {/* Backdrop - click outside to close */}
      <div 
        className={`fixed left-0 right-0 bottom-0 z-30 transition-opacity duration-150 ${
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{ top: '48px', backgroundColor: 'rgba(0,0,0,0.15)' }}
        onClick={onToggle}
      />

      {/* Sidebar - always overlay, never push content */}
      <div 
        className={`
          fixed left-0 top-12 bottom-0 bg-white
          transition-transform duration-200 ease-out z-40
          ${open ? 'translate-x-0' : '-translate-x-full'}
          w-64 flex flex-col border-r
        `} 
        style={{ 
          borderColor: '#F3F4F6', 
          boxShadow: open ? '4px 0 24px rgba(0,0,0,0.08)' : 'none'
        }}
      >
        {/* New Chat Button */}
        <div className="p-3">
          <button
            onClick={onNewChat}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition btn-glide tap-feedback"
          >
            <Plus size={16} className="text-gray-500" />
            <span className="text-sm font-medium text-gray-700">New chat</span>
          </button>
        </div>

        {/* Chat History */}
        <div className="flex-1 overflow-y-auto px-2">
          {user ? (
            <div className="space-y-1">
              {conversations.length > 0 ? conversations.map(conv => (
                <div
                  key={conv.id}
                  className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition row-highlight ${
                    conv.active ? 'bg-gray-100' : ''
                  }`}
                  onClick={() => onSelectConversation(conv.id)}
                >
                  <MessageCircle size={14} className="text-gray-400 flex-shrink-0" />
                  <span className={`text-sm truncate flex-1 ${conv.active ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>
                    {conv.title}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteConversation(conv.id)
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-gray-200 transition"
                  >
                    <Trash2 size={12} className="text-gray-400" />
                  </button>
                </div>
              )) : (
                <p className="text-xs text-gray-400 text-center py-8">No conversations yet</p>
              )}
            </div>
          ) : (
            <div className="text-center py-12 px-4">
              <MessageCircle size={32} className="text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500 mb-1">Sign in to save chats</p>
              <p className="text-xs text-gray-400">Your history will sync across devices</p>
            </div>
          )}
        </div>

        {/* Favorites Section - placeholder */}
        {user && (
          <div className="px-3 py-2 border-t" style={{ borderColor: '#F3F4F6' }}>
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">My Teams</p>
            <div className="flex flex-wrap gap-1.5">
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-md flex items-center gap-1">
                <Heart size={10} className="text-pink-400" />
                Lakers
              </span>
              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-md flex items-center gap-1">
                <Heart size={10} className="text-pink-400" />
                Warriors
              </span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="p-3 border-t" style={{ borderColor: '#F3F4F6' }}>
          <p className="text-[10px] text-gray-300 text-center">
            Powered by Orb
          </p>
        </div>
      </div>
    </>
  )
}
